import { useAlert, useGlobals, useSync } from '@/hooks';
import * as Sentry from '@sentry/react';
import * as R from '@/constants/systemRoutes';
import { fullscreenLoadingMessages } from '@/constants/fullscreenLoadingMessages';
import { useUserContext } from '@/hooks';
import { useMutation } from '@apollo/client';
import DEFAULT_OPERATIONS from './signIn.gql';
import { useNetworkStatusContext } from '@/hooks';
import { tableKeys } from '@/shared/storageFunctions';
import { validateCNPJ } from '@/shared/formValidator';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import { useNavigate } from 'react-router-dom';
import { collectPWAMetrics, useEvents } from '../use-events/useEvents';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

interface ErrorControl {
  error: boolean;
  message?: string;
}

interface RCALoginOutput {
  aws_data: string;
  aws_token: string;
  seqpessoa: string;
  magento_token: string;
  tipo_usuario?: string;
}

interface ResponseAPI {
  data: {
    RCALogin: RCALoginOutput;
  };
  errors?: any;
}

interface LoadingFeedback {
  message: string;
  isSlow: boolean;
}

export const useSignIn = () => {
  const db = useDbFunctions();
  const { deleteDB } = db;
  const { showAlert } = useAlert();
  const { syncDatasets } = useSync();
  const { setIsOpenResetPasswordModal } = useGlobals();
  const { registerEvent, registerVersionEvent } = useEvents(db);

  const { signInMutation } = DEFAULT_OPERATIONS;
  const navigate = useNavigate();
  const { connectivityStatus, checkConnectivity } = useNetworkStatusContext();
  const { setIsSignedIn, getCustomer, setEmail } = useUserContext();

  const [inputValue, setInputValue] = useState<string>('');
  const [passwordValue, setPasswordValue] = useState<string>('');
  const [inputMaxValue, setInputMaxValue] = useState<number>(100);

  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [taxVatValid, setTaxVatValid] = useState<boolean>(false);
  const [taxVatExist, setTaxVatExist] = useState<boolean>(false);
  const [isContinueDisabled, setIsContinueDisabled] = useState(false);

  const [passwordValid, setPasswordValid] = useState<boolean>(false);

  const [inputError, setInputError] = useState<ErrorControl>({ error: false });
  const [loginError, setLoginError] = useState<ErrorControl | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState<LoadingFeedback | null>(null);

  const slowServerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const cnpjRegex = /^\d{14}$/;

  const [signIn, { loading: loadingSignIn }] = useMutation(signInMutation, {
    fetchPolicy: 'no-cache',
  });

  const clearLoadingFeedbackTimers = useCallback(() => {
    if (slowServerTimerRef.current) {
      clearTimeout(slowServerTimerRef.current);
      slowServerTimerRef.current = null;
    }
  }, []);

  const updateLoadingStage = useCallback((message: string, isSlow = false) => {
    setLoadingFeedback({ message, isSlow });
  }, []);

  const handleLoginInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trim();
      setInputValue(value);

      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length === 14) {
        if (cnpjRegex.test(cleanValue)) {
          setInputMaxValue(14);
          const formatted = handleFormatCNPJ(cleanValue);

          setInputValue(formatted);
          setTaxVatValid(true);
          setEmailValid(false);
          return;
        }
      } else {
        setTaxVatValid(false);
        setInputMaxValue(100);
      }

      if (emailRegex.test(value)) {
        setInputMaxValue(100);
        setEmailValid(true);
      } else {
        setEmailValid(false);
      }
    },
    [emailRegex, cnpjRegex]
  );

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    setPasswordValue(password);

    if (password) {
      setPasswordValid(true);
    } else {
      setPasswordValid(false);
    }
  };

  const handleFormatCNPJ = (cnpj: string) => {
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length === 14) {
      return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
    }
    return cnpj;
  };

  const getUserNode = (response: any) => {
    const tipoUsuario = response.tipo_usuario;
    return response[tipoUsuario.toLowerCase()] || null;
  };

  const saveAwsData = useCallback(
    async (awsData: any) => {
      awsData = JSON.parse(awsData);
      const res = await getCustomer();

      if (res?.data?.customer) {
        const { data } = res;
        const { customer } = data;

        const rcaData = {
          tipoUsuario: awsData?.tipo_usuario,
          firstname: customer?.firstname,
          lastname: customer?.lastname,
          nomeUsuario: awsData?.nomeUsuario,
          codPessoa: awsData?.cod_pessoa,
          codSegmento: awsData?.representante?.[0]?.cod_segmento,
          fechaPedido: awsData?.fecha_pedido,
          viewDashboard: awsData?.view_dashboard,
          email: customer?.email,
          phone: customer?.personal_phone,
          taxvat: customer?.taxvat,
        };

        const dataSets = getUserNode(awsData)[0].link_Dataset ?? getUserNode(awsData)[0].link_Dashboard;

        if (rcaData && dataSets) {
          setOnStorage('rca_usuario', rcaData);
          setOnStorage('user_logado', rcaData);
          setOnStorage('new_data_sets', dataSets);
        }
      }
    },
    [getCustomer]
  );

  const clearUser = useCallback(async () => {
    tableKeys.forEach((key) => removeFromStorage(key));
    await deleteDB();
  }, [deleteDB]);

  const confirmNewUser = useCallback(async (shouldClear = true): Promise<boolean> => {
    if (
      !window.confirm('Você está entrando com um usuário diferente do último acesso. Ao continuar, os carrinhos e dados da sessão anterior serão removidos. Deseja continuar?'
      )
    ) {
      return false;
    }
    if (shouldClear) {
      await clearUser();
    }
    return true;
  }, [clearUser]);

  const handleSignIn = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setLoginError(null);
      
      const isOnline = await checkConnectivity();
      const loginDate = new Date();
      const lastLogin = getOnStorage('login');

      if (lastLogin && inputValue !== lastLogin) {
        const resp = await confirmNewUser(false);
        if (!resp) return;
        if (!isOnline) {
          showAlert({
            title: 'Você está offline.',
            message: 'Por favor, verifique sua conexão e tente novamente.',
            theme: 'warning',
            time: 15000,
            bordered: true,
            close: 'icon',
            topPosition: 75,
          });
          return;
        }
        await clearUser();
      }

      setOnStorage('login_date', loginDate);
      setOnStorage('login', inputValue);
      setIsContinueDisabled(true);

      if (!isOnline) {
        const token_offline = getOnStorage('token_offline');
        if (token_offline) {
          Sentry.captureException('Tem token offline');
          setOnStorage('token_magento', token_offline);
          setOnStorage('token_offline', null);
          setIsSignedIn(true);
          localStorage.setItem('login_bootstrap', 'done');
          navigate(R.DASHBOARD.link);
          window.location.reload();
        } else {
          setIsContinueDisabled(false);
          showAlert({
            title: 'Você está offline.',
            message: 'Por favor, verifique sua conexão e tente novamente.',
            theme: 'warning',
            time: 15000,
            bordered: true,
            close: 'icon',
            topPosition: 75,
          });
        }
      } else {
        try {
          const variables = emailValid
            ? { email: inputValue, password: passwordValue }
            : taxVatValid
              ? { taxvat: inputValue, password: passwordValue }
              : null;

          if (!variables) {
            setLoginError({
              error: true,
              message: 'Dados inválidos. Verifique e tente novamente.',
            });
            return;
          }

          const res = (await signIn({ variables })) as ResponseAPI;
          const oldCodPessoa = getOnStorage('cod_pessoa');
          
          if (oldCodPessoa && oldCodPessoa !== res.data.RCALogin.seqpessoa) {
            const isNewUser = await confirmNewUser();
            if (!isNewUser) return setIsContinueDisabled(false);
          }
          
          setOnStorage('login_date', new Date());
          setOnStorage('user_login_data', JSON.stringify(res));

          if (res && !res?.errors) {
            updateLoadingStage(fullscreenLoadingMessages.signIn.loadingAccount, true);

            try {
              localStorage.setItem('login_bootstrap', 'in-progress');
              removeFromStorage('token_offline');
            } catch {}

            const seqPessoa = Number(res.data.RCALogin.seqpessoa);
            const RCALogin = res?.data?.RCALogin;

            if (RCALogin?.magento_token && RCALogin?.aws_data) {
              updateLoadingStage(fullscreenLoadingMessages.signIn.loadingAppContent, true);
              setOnStorage('token_magento', RCALogin?.magento_token);
              await saveAwsData(RCALogin?.aws_data);
            }

            if (RCALogin?.seqpessoa) {
              setOnStorage('cod_pessoa', RCALogin.seqpessoa);
            }

            await Promise.all([
              registerEvent({
                tipoEvento: 'LOGIN',
                obsEvento: `Login: ${inputValue}`,
                codRca: seqPessoa,
              }).catch(() => {}),
              registerVersionEvent(seqPessoa).catch(() => {}),
              registerEvent({
                tipoEvento: 'HARDWARE',
                obsEvento: `Hardware: ${JSON.stringify(await collectPWAMetrics())}`,
                codRca: seqPessoa,
              }).catch(() => {}),
            ]);
            
            if (RCALogin?.aws_data) {
              setOnStorage('temporary_token_aws', RCALogin.aws_token);
              updateLoadingStage(fullscreenLoadingMessages.signIn.syncingData, true);
              await syncDatasets({ isBackground: false, codPessoa: RCALogin.seqpessoa });
            }

            if (RCALogin?.aws_token) {
              removeFromStorage('temporary_token_aws');
              setOnStorage('token_aws', RCALogin.aws_token);
              updateLoadingStage(fullscreenLoadingMessages.signIn.finishing, true);
              await new Promise((resolve) => setTimeout(resolve, 100));

              try {
                localStorage.setItem('login_bootstrap', 'done');
              } catch {}

              const goToDashboard = () => {
                navigate(R.DASHBOARD.link);
                setIsSignedIn(true);
              };

              if ('serviceWorker' in navigator) {
                if (!navigator.serviceWorker.controller) {
                  navigator.serviceWorker.addEventListener(
                    'controllerchange',
                    () => {
                      goToDashboard();
                    },
                    { once: true }
                  );
                } else {
                  goToDashboard();
                }
              } else {
                goToDashboard();
              }

              console.log('Login bem-sucedido, redirecionando para dashboard...');

              window?.clarity?.("identify", String(RCALogin.seqpessoa));
              window?.clarity?.("set", "codigo_rca", String(RCALogin.seqpessoa));
              window?.clarity?.("set", "perfil_usuario", RCALogin.tipo_usuario);

              // await unregisterServiceWorker({ preserveLocalStorage: true });
              window.location.reload();
            }
          }
        } catch (e) {
          console.error('Erro no fluxo de login:', e);
          localStorage.removeItem('login_bootstrap');
          setLoginError({
            error: true,
            message: 'Não foi possível fazer login. Verifique os dados e tente novamente.',
          });
          setLoadingFeedback(null);
          setIsContinueDisabled(false);
        }
      }
    },
    [
      inputValue,
      emailValid,
      taxVatValid,
      passwordValue,
      signIn,
      confirmNewUser,
      saveAwsData,
      setIsSignedIn,
      connectivityStatus,
      updateLoadingStage,
    ]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearLoadingFeedbackTimers();
    };
  }, [clearLoadingFeedbackTimers]);

  useEffect(() => {
    if (!loadingSignIn) {
      clearLoadingFeedbackTimers();
      setLoadingFeedback(null);
      return;
    }

    clearLoadingFeedbackTimers();

    setLoadingFeedback({
      message: fullscreenLoadingMessages.signIn.validatingCredentials,
      isSlow: false,
    });

    slowServerTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      setLoadingFeedback({
        message: fullscreenLoadingMessages.signIn.slowServer,
        isSlow: true,
      });
    }, 6000);

    return () => {
      clearLoadingFeedbackTimers();
    };
  }, [loadingSignIn, clearLoadingFeedbackTimers]);

  useEffect(() => {
    if (taxVatValid && inputValue) {
      const isCnpjValid = validateCNPJ(inputValue);
      setTaxVatExist(isCnpjValid);
    } else {
      setTaxVatExist(false);
    }
  }, [emailValid, taxVatValid, inputValue]);

  useEffect(() => {
    if (!taxVatExist && inputValue && taxVatValid) {
      setInputError({
        error: true,
        message: 'Este CNPJ não existe. Tente novamente!',
      });
    } else {
      setInputError({
        error: false,
      });
    }
  }, [taxVatValid, inputValue, taxVatExist]);

  const handleRedirect = useCallback(() => {
    if (inputValue && emailValid) {
      setEmail(inputValue);
      setIsOpenResetPasswordModal(true);
    } else {
      setLoginError({
        error: true,
        message: 'Verifique seu endereço de e-mail para prosseguir com o reset de senha.',
      });
    }
  }, [setLoginError, setEmail, setIsOpenResetPasswordModal, emailValid, inputValue]);

  return {
    emailValid,
    inputValue,
    loginError,
    inputError,
    taxVatExist,
    passwordValue,
    passwordValid,
    inputMaxValue,
    loadingFeedback,
    isContinueDisabled,
    loading: loadingSignIn,
    handleSignIn,
    setIsSignedIn,
    handleRedirect,
    handleLoginInput,
    handlePasswordChange,
  };
};
